import { Button, cn, Dialog } from '@alicerce/ui';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, CheckCircle2, ChevronDown, ChevronUp, Circle, Compass, Hourglass, Lock, Minus, PartyPopper, Sparkles, X } from 'lucide-react';
import { useCallback, useEffect, useId, useReducer, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSession } from '@/lib/session';
import { setupPrefs } from '@/lib/storage';
import { isOnStepPage, newlyDone, type SetupState, type SetupStep } from './steps';
import { Spotlight, WELCOME_TOUR } from './tour';
import { SETUP_KEY, useSetup } from './use-setup';
import './setup.css';

/** Abre o tour de boas-vindas de qualquer tela (ex.: Ajuda). */
export const START_TOUR_EVENT = 'gestora:start-tour';
export function startTour() {
  window.dispatchEvent(new Event(START_TOUR_EVENT));
}

function ProgressTrack({ done, total, className }: { done: number; total: number; className?: string }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div
      role="progressbar"
      aria-label="Configuração do negócio"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={done}
      aria-valuetext={`${done} de ${total} passos concluídos`}
      className={cn('h-2.5 overflow-hidden rounded-full bg-mist', className)}
    >
      <div className="setup-fill h-full rounded-full bg-gradient-to-r from-brand-600 to-violet" style={{ width: `${pct}%` }} />
    </div>
  );
}

function StepStatus({ step, state }: { step: SetupStep; state: SetupState }) {
  if (step.done) return <CheckCircle2 className="size-5 shrink-0 text-success" aria-label="Concluído" />;
  if (step.waiting) return <Hourglass className="size-5 shrink-0 text-warning" aria-label="Em análise" />;
  if (step.locked) return <Lock className="size-5 shrink-0 text-muted" aria-label="Bloqueado" />;
  return <Circle className={cn('size-5 shrink-0', state.next?.key === step.key ? 'text-violet' : 'text-slate-300')} aria-label="Pendente" />;
}

function StepList({ state, onGo }: { state: SetupState; onGo: (s: SetupStep) => void }) {
  const titleOf = (key: string) => state.steps.find((s) => s.key === key)?.guide.title ?? key;
  return (
    <ol className="grid gap-2 sm:grid-cols-2">
      {state.steps.map((s, i) => (
        <li key={s.key} className={cn('flex items-start gap-3 rounded-2xl border border-line/70 bg-white/70 p-3', state.next?.key === s.key && 'border-violet/40 ring-1 ring-violet/20')}>
          <StepStatus step={s} state={state} />
          <div className="min-w-0 flex-1">
            <p className={cn('text-sm font-medium', s.done && 'text-muted line-through')}>
              <span className="sr-only">Passo {i + 1}: </span>
              {s.guide.title}
            </p>
            {s.waiting && <p className="text-xs text-warning">Em análise. A gente avisa quando terminar.</p>}
            {s.locked && <p className="text-xs text-muted">Libera depois de: {(s.guide.requires ?? []).map(titleOf).join(', ')}</p>}
            {!s.done && !s.waiting && !s.locked && s.guide.why && <p className="text-xs text-muted">{s.guide.why}</p>}
          </div>
          {!s.done && !s.locked && s.link && (
            <Button size="sm" variant={state.next?.key === s.key ? 'primary' : 'secondary'} onClick={() => onGo(s)} aria-label={`Ir para ${s.guide.title}`}>
              {s.waiting ? 'Ver' : 'Ir'}
            </Button>
          )}
        </li>
      ))}
    </ol>
  );
}

/** Barra do topo: quanto falta, qual o próximo passo e a lista completa. */
function ProgressBar({
  state,
  onGo,
  onTour,
  collapsed,
  setCollapsed,
  onShowCoach,
  coachHidden,
}: {
  state: SetupState;
  onGo: (s: SetupStep) => void;
  onTour: () => void;
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  onShowCoach: () => void;
  coachHidden: boolean;
}) {
  const [listOpen, setListOpen] = useState(false);
  const { pathname } = useLocation();
  const listId = useId();
  const next = state.next;
  const here = next ? isOnStepPage(pathname, next) : false;
  const waiting = state.steps.filter((s) => s.waiting);

  if (collapsed) {
    return (
      <div className="mx-3 mt-3 flex justify-end sm:mx-6">
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="inline-flex items-center gap-2 rounded-full border border-white bg-white/80 px-3 py-1.5 text-sm font-medium shadow-[0_8px_24px_-18px_rgb(30_58_95/0.6)] backdrop-blur-xl hover:bg-white"
        >
          <Sparkles className="size-4 text-violet" aria-hidden />
          Configuração {state.done}/{state.total}
          <span className="text-brand-700">· Continuar</span>
        </button>
      </div>
    );
  }

  return (
    <section
      data-tour="setup-bar"
      aria-label="Configuração do negócio"
      className="setup-pop mx-3 mt-3 rounded-card border border-white bg-white/75 p-3 shadow-[0_12px_32px_-24px_rgb(30_58_95/0.5)] backdrop-blur-xl backdrop-saturate-150 sm:mx-6 sm:p-4"
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-5">
        <div className="flex min-w-0 items-center gap-3 md:w-72 md:shrink-0">
          <span aria-hidden className="grid size-10 shrink-0 place-items-center rounded-2xl bg-violet-50 text-violet">
            <Sparkles className="size-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">Deixe sua loja pronta</p>
            <div className="mt-1.5 flex items-center gap-2">
              <ProgressTrack done={state.done} total={state.total} className="flex-1" />
              <span className="text-xs font-medium whitespace-nowrap text-muted tabular-nums">
                {state.done} de {state.total}
              </span>
            </div>
          </div>
        </div>
        <div className="min-w-0 flex-1 text-sm">
          {next ? (
            <p className="truncate">
              <span className="text-muted">Próximo passo: </span>
              <span className="font-medium">{next.guide.title}</span>
              {next.guide.why && <span className="hidden text-muted xl:inline"> · {next.guide.why}</span>}
            </p>
          ) : waiting.length ? (
            <p className="text-muted">
              Agora é só aguardar: <span className="font-medium text-ink">{waiting.map((s) => s.guide.title).join(', ')}</span> em análise.
            </p>
          ) : (
            <p className="text-muted">Os passos que faltam dependem de outros. Veja a lista.</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {next &&
            (here && coachHidden ? (
              <Button data-tour="setup-next" onClick={onShowCoach}>
                <Compass className="size-4" aria-hidden /> Como preencher
              </Button>
            ) : (
              <Button data-tour="setup-next" onClick={() => onGo(next)} disabled={here}>
                {here ? 'Você está aqui' : 'Próximo passo'} {!here && <ArrowRight className="size-4" aria-hidden />}
              </Button>
            ))}
          <Button variant="secondary" aria-expanded={listOpen} aria-controls={listId} onClick={() => setListOpen((v) => !v)}>
            {listOpen ? <ChevronUp className="size-4" aria-hidden /> : <ChevronDown className="size-4" aria-hidden />}
            Ver todos
          </Button>
          <Button variant="ghost" size="sm" aria-label="Recolher a barra de configuração" title="Recolher" onClick={() => setCollapsed(true)}>
            <Minus className="size-4" aria-hidden />
          </Button>
        </div>
      </div>
      {listOpen && (
        <div id={listId} className="mt-4 border-t border-line/70 pt-4">
          <StepList
            state={state}
            onGo={(s) => {
              setListOpen(false);
              onGo(s);
            }}
          />
          <div className="mt-3 flex justify-end">
            <Button variant="ghost" size="sm" onClick={onTour}>
              <Compass className="size-4" aria-hidden /> Rever o tour do painel
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

/** Cartão flutuante com as instruções do passo, enquanto a pessoa está na página dele. */
function StepCoach({ step, state, onSkip, onClose }: { step: SetupStep; state: SetupState; onSkip: () => void; onClose: () => void }) {
  const [min, setMin] = useState(false);
  const titleId = useId();
  const position = state.steps.findIndex((s) => s.key === step.key) + 1;
  return (
    <aside
      aria-labelledby={titleId}
      className="setup-pop fixed inset-x-2 bottom-2 z-40 rounded-tile border border-white bg-white/95 shadow-[0_24px_60px_-24px_rgb(16_34_58/0.55)] backdrop-blur-xl sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[24rem]"
    >
      <div className="flex items-center gap-3 px-4 pt-4 pb-2">
        <span aria-hidden className="grid size-9 shrink-0 place-items-center rounded-full bg-brand-600 text-sm font-semibold text-white">
          {position}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold tracking-wide text-violet uppercase">
            Passo {position} de {state.total}
          </p>
          <h2 id={titleId} className="truncate text-base font-semibold">
            {step.guide.title}
          </h2>
        </div>
        <button
          type="button"
          onClick={() => setMin((v) => !v)}
          aria-expanded={!min}
          aria-label={min ? 'Mostrar instruções' : 'Esconder instruções'}
          className="rounded-full p-1.5 text-muted hover:bg-mist hover:text-ink"
        >
          {min ? <ChevronUp className="size-4" aria-hidden /> : <ChevronDown className="size-4" aria-hidden />}
        </button>
        <button type="button" onClick={onClose} aria-label="Fechar o guia" className="rounded-full p-1.5 text-muted hover:bg-mist hover:text-ink">
          <X className="size-4" aria-hidden />
        </button>
      </div>
      {!min && (
        <div className="px-4 pb-4">
          {step.waiting ? (
            <p className="rounded-2xl bg-amber-50 p-3 text-sm leading-relaxed text-amber-900">{step.guide.waitingText ?? 'Recebemos! Agora é só aguardar a análise.'}</p>
          ) : (
            <>
              {step.guide.why && <p className="text-sm leading-relaxed text-ink/80">{step.guide.why}</p>}
              {step.guide.howTo.length > 0 && (
                <ol className="mt-3 space-y-2">
                  {step.guide.howTo.map((h, i) => (
                    <li key={h} className="flex gap-2.5 text-sm leading-relaxed">
                      <span aria-hidden className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-violet-50 text-[11px] font-semibold text-violet">
                        {i + 1}
                      </span>
                      <span>{h}</span>
                    </li>
                  ))}
                </ol>
              )}
              <p className="mt-3 text-xs text-muted">Quando terminar, a barra lá em cima atualiza sozinha.</p>
            </>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              Sair do guia
            </Button>
            {!step.waiting && (
              <Button variant="secondary" size="sm" onClick={onSkip}>
                Pular por agora
              </Button>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}

function Confetti() {
  const colors = ['#5700C7', '#1E3A5F', '#B48CFF', '#13795B', '#F59E0B'];
  return (
    <div aria-hidden className="setup-confetti pointer-events-none absolute inset-0 overflow-visible">
      {Array.from({ length: 18 }, (_, i) => {
        const angle = (i / 18) * Math.PI * 2;
        const dist = 70 + (i % 3) * 25;
        return (
          <span
            key={i}
            className="absolute top-1/2 left-1/2 block size-2 rounded-[2px]"
            style={
              {
                background: colors[i % colors.length],
                '--x': `${Math.cos(angle) * dist}px`,
                '--y': `${Math.sin(angle) * dist}px`,
                '--r': `${(i % 2 ? 1 : -1) * 220}deg`,
              } as React.CSSProperties
            }
          />
        );
      })}
    </div>
  );
}

/** Parabéns ao concluir um passo, dizendo o que a pessoa ganhou com ele e oferecendo o próximo. */
function Celebration({ step, state, onNext, onClose }: { step: SetupStep; state: SetupState; onNext: (s: SetupStep) => void; onClose: () => void }) {
  const next = state.next;
  return (
    <Dialog
      open
      onClose={onClose}
      title={state.complete ? 'Tudo pronto!' : step.guide.celebrate.title}
      footer={
        next ? (
          <>
            <Button variant="ghost" className="whitespace-nowrap" onClick={onClose}>
              Agora não
            </Button>
            <Button className="whitespace-nowrap" onClick={() => onNext(next)} aria-label={`Próximo: ${next.guide.title}`}>
              <span className="sm:hidden">Próximo passo</span>
              <span className="hidden sm:inline">Próximo: {next.guide.title}</span> <ArrowRight className="size-4" aria-hidden />
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Continuar</Button>
        )
      }
    >
      <div className="relative flex flex-col items-center py-2 text-center">
        <Confetti />
        <span className="setup-badge grid size-16 place-items-center rounded-full bg-gradient-to-br from-brand-600 to-violet text-white shadow-lg">
          <PartyPopper className="size-8" aria-hidden />
        </span>
        <p className="mt-4 text-[15px] leading-relaxed">{step.guide.celebrate.message}</p>
        {state.complete && <p className="mt-2 text-sm text-muted">Você concluiu toda a configuração. Bom trabalho e boas vendas!</p>}
        {!state.complete && (
          <div className="mt-4 w-full">
            <ProgressTrack done={state.done} total={state.total} />
            <p className="mt-1.5 text-xs text-muted">
              {state.done} de {state.total} passos concluídos
            </p>
          </div>
        )}
      </div>
    </Dialog>
  );
}

function Welcome({ name, state, onStart, onSkip }: { name: string; state: SetupState; onStart: () => void; onSkip: () => void }) {
  const remaining = state.total - state.done;
  return (
    <Dialog
      open
      onClose={onSkip}
      title={`Boas-vindas à Gestora${name ? `, ${name}` : ''}!`}
      footer={
        <>
          <Button variant="ghost" onClick={onSkip}>
            Prefiro explorar sozinho
          </Button>
          <Button onClick={onStart}>
            Fazer o tour <ArrowRight className="size-4" aria-hidden />
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-[15px] leading-relaxed">
        <p>Vamos deixar seu negócio pronto para vender, um passo de cada vez.</p>
        <p>
          Primeiro mostramos onde fica cada coisa no painel. Depois levamos você até cada página, explicando o que preencher. Faltam <strong>{remaining}</strong>{' '}
          {remaining === 1 ? 'passo' : 'passos'}, e dá para parar e continuar quando quiser.
        </p>
      </div>
    </Dialog>
  );
}

/**
 * Configuração guiada do painel: tour de boas-vindas no primeiro acesso, barra de progresso no topo,
 * guia na página de cada passo e parabéns quando um passo fica pronto.
 */
export function SetupGuide() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const [guiding, setGuidingState] = useState<string | null>(() => setupPrefs.guiding());
  const [tour, setTour] = useState<'welcome' | 'spotlight' | null>(null);
  // As preferências ficam no navegador; isto só pede um novo render depois de gravá-las.
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const { enabled, state, userId, tenantId, skip } = useSetup({
    // Durante o guia, confere sozinho de tempos em tempos (ex.: documento aprovado, Melhor Envio conectado).
    pollMs: guiding ? 10_000 : false,
  });
  const firstName = useSession().me?.user.name.trim().split(/\s+/)[0] ?? '';

  const setGuiding = useCallback((key: string | null) => {
    setupPrefs.setGuiding(key);
    setGuidingState(key);
  }, []);

  // Cada troca de tela confere de novo o que já foi feito.
  const firstPath = useRef(true);
  useEffect(() => {
    if (firstPath.current) {
      firstPath.current = false;
      return;
    }
    void queryClient.invalidateQueries({ queryKey: SETUP_KEY });
  }, [pathname, queryClient]);

  // No primeiro acesso deste navegador, o que já estava feito não ganha parabéns: só fica anotado.
  const seen = enabled ? setupPrefs.celebrated(userId, tenantId) : null;
  useEffect(() => {
    if (state && state.total > 0 && enabled && setupPrefs.celebrated(userId, tenantId) === null) {
      setupPrefs.setCelebrated(userId, tenantId, state.steps.filter((s) => s.done).map((s) => s.key));
    }
  }, [state, enabled, userId, tenantId]);

  useEffect(() => {
    const open = () => setTour('welcome');
    window.addEventListener(START_TOUR_EVENT, open);
    return () => window.removeEventListener(START_TOUR_EVENT, open);
  }, []);

  if (!enabled || !state || state.total === 0) return null;

  // Primeiro acesso de quem configura: tour de boas-vindas.
  const activeTour = tour ?? (!state.complete && !setupPrefs.tourSeen(userId, tenantId) ? 'welcome' : null);
  const fresh = seen === null ? [] : newlyDone(seen, state.steps);
  const celebrating = activeTour ? undefined : fresh[0];

  const markCelebrated = (keys: string[]) => {
    setupPrefs.setCelebrated(userId, tenantId, [...new Set([...(setupPrefs.celebrated(userId, tenantId) ?? []), ...keys])]);
    rerender();
  };

  const collapsed = setupPrefs.collapsed(userId, tenantId);
  const setCollapsed = (v: boolean) => {
    setupPrefs.setCollapsed(userId, tenantId, v);
    rerender();
  };

  const go = (step: SetupStep) => {
    if (fresh.length) markCelebrated(fresh.map((s) => s.key));
    setGuiding(step.key);
    if (step.link && !isOnStepPage(pathname, step)) navigate(step.link);
  };

  const endTour = (thenGo: boolean) => {
    setupPrefs.setTourSeen(userId, tenantId);
    setTour(null);
    rerender();
    if (thenGo && state.next) go(state.next);
  };

  // O passo guiado que ficou pronto sai de cena; os parabéns oferecem o próximo.
  const guided = guiding ? state.steps.find((s) => s.key === guiding && !s.done) : undefined;
  const showCoach = !!guided && isOnStepPage(pathname, guided) && !activeTour && !celebrating;

  return (
    <>
      {!state.complete && (
        <ProgressBar
          state={state}
          onGo={go}
          onTour={() => setTour('welcome')}
          collapsed={collapsed && activeTour !== 'spotlight'}
          setCollapsed={setCollapsed}
          coachHidden={!showCoach}
          onShowCoach={() => {
            if (state.next) setGuiding(state.next.key);
          }}
        />
      )}
      {showCoach && guided && (
        <StepCoach
          step={guided}
          state={state}
          onClose={() => setGuiding(null)}
          onSkip={() => {
            skip(guided.key);
            // O próximo é escolhido de novo já sem o passo pulado.
            const after = state.steps.find((s) => s.key !== guided.key && !s.done && !s.waiting && !s.locked && !s.skipped);
            if (after) go(after);
            else setGuiding(null);
          }}
        />
      )}
      {celebrating && (
        <Celebration
          step={celebrating}
          state={state}
          onClose={() => {
            markCelebrated([celebrating.key]);
            if (guiding && !guided) setGuiding(null);
          }}
          onNext={go}
        />
      )}
      {activeTour === 'welcome' && <Welcome name={firstName} state={state} onStart={() => setTour('spotlight')} onSkip={() => endTour(false)} />}
      {activeTour === 'spotlight' && (
        <Spotlight stops={WELCOME_TOUR} onClose={() => endTour(false)} onFinish={() => endTour(true)} finishLabel={state.next ? 'Começar a configurar' : 'Concluir'} />
      )}
      <div aria-live="polite" className="sr-only">
        {celebrating ? `${celebrating.guide.celebrate.title} ${celebrating.guide.celebrate.message}` : ''}
      </div>
    </>
  );
}
